import React, { useState } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Grid, Button, alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX } from '../../common/tableStyles';
import LaptopIcon from '@mui/icons-material/Laptop';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import { getMediaUrl } from '../../../utils/urlHelper';
import { Label, GlassCard } from '../../StyledComponents/StyledComponent';

const ActionButton = styled(Button)(() => ({
    backgroundColor: alpha('#3DAC5A', 0.1),
    color: '#3DAC5A',
    textTransform: 'none',
    borderRadius: '12px',
    padding: '8px 20px',
    fontWeight: 700,
    border: `1px solid ${alpha('#3DAC5A', 0.2)}`,
    '&:hover': {
        backgroundColor: '#3DAC5A',
        color: '#fff',
        boxShadow: `0 8px 16px ${alpha('#3DAC5A', 0.2)}`,
    },
    transition: 'all 0.3s ease',
}));

const UploadPlaceholder = styled(Box)(({ hasFile }) => ({
    width: '100%',
    padding: '32px',
    border: `2px dashed ${hasFile ? '#3DAC5A' : '#e2e8f0'}`,
    borderRadius: '20px',
    backgroundColor: hasFile ? alpha('#3DAC5A', 0.02) : alpha('#000', 0.02),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
        borderColor: '#3DAC5A',
        backgroundColor: alpha('#3DAC5A', 0.05),
    },
}));

const AssetTab = ({ assets = [], onAddAsset, onDeleteAsset }) => {
    const [open, setOpen] = useState(false);
    const [newAsset, setNewAsset] = useState({
        name: '',
        quantity: 1,
        description: '',
        date_provided: '',
    });
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewAsset(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = () => {
        onAddAsset(newAsset, file);
        setOpen(false);
        setNewAsset({ name: '', quantity: 1, description: '', date_provided: '' });
        setFile(null);
    };

    return (
        <GlassCard sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LaptopIcon sx={{ color: '#3DAC5A' }} />
                        Company Assets
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        List of hardware and equipment assigned to the employee.
                    </Typography>
                </Box>
                <ActionButton startIcon={<AddIcon />} onClick={() => setOpen(true)}>
                    Assign Asset
                </ActionButton>
            </Box>

            <TableContainer sx={TABLE_PAPER_SX}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={HEAD_CELL_SX}>Asset Name</TableCell>
                            <TableCell sx={HEAD_CELL_SX}>Quantity</TableCell>
                            <TableCell sx={HEAD_CELL_SX}>Provided Date</TableCell>
                            <TableCell sx={HEAD_CELL_SX}>Description</TableCell>
                            <TableCell sx={HEAD_CELL_SX}>Document</TableCell>
                            <TableCell sx={HEAD_CELL_SX}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {assets.length > 0 ? assets.map((asset) => (
                            <TableRow key={asset.id} sx={TABLE_ROW_SX}>
                                <TableCell sx={{ ...BODY_CELL_SX, fontWeight: 600 }}>{asset.name}</TableCell>
                                <TableCell sx={BODY_CELL_SX}>{asset.quantity}</TableCell>
                                <TableCell sx={BODY_CELL_SX}>{asset.date_provided || 'N/A'}</TableCell>
                                <TableCell sx={BODY_CELL_SX}>{asset.description || '-'}</TableCell>
                                <TableCell sx={BODY_CELL_SX}>
                                    {asset.attachment ? (
                                        <IconButton
                                            onClick={() => setPreviewUrl(getMediaUrl(asset.attachment))}
                                            sx={{ color: '#3DAC5A', bgcolor: alpha('#3DAC5A', 0.1), '&:hover': { bgcolor: alpha('#3DAC5A', 0.2) } }}
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    ) : (
                                        <Typography variant="caption" sx={{ color: '#cbd5e1' }}>No Document</Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={BODY_CELL_SX}>
                                    <IconButton color="error" onClick={() => onDeleteAsset(asset.id)} sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}>
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 8, color: '#94a3b8' }}>
                                    No assets assigned to this employee.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800, fontSize: '1.25rem' }}>Assign New Asset</DialogTitle>
                <DialogContent>
                    <Grid container spacing={3} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <Label>Asset Name</Label>
                            <TextField
                                fullWidth
                                name="name"
                                value={newAsset.name}
                                onChange={handleInputChange}
                                placeholder="e.g. MacBook Pro M2"
                                variant="outlined"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Label>Quantity</Label>
                            <TextField
                                fullWidth
                                name="quantity"
                                type="number"
                                value={newAsset.quantity}
                                onChange={handleInputChange}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Label>Provided Date</Label>
                            <TextField
                                fullWidth
                                name="date_provided"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={newAsset.date_provided}
                                onChange={handleInputChange}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Label>Serial No / Description</Label>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                name="description"
                                value={newAsset.description}
                                onChange={handleInputChange}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Label>Receiving Document</Label>
                            <UploadPlaceholder
                                component="label"
                                hasFile={!!file}
                            >
                                <input type="file" hidden onChange={(e) => setFile(e.target.files[0])} />
                                <UploadFileIcon sx={{ fontSize: 40, color: file ? '#3DAC5A' : '#cbd5e1' }} />
                                <Typography variant="body2" sx={{ fontWeight: 700, color: file ? '#3DAC5A' : '#64748b' }}>
                                    {file ? file.name : 'Click to select receiving doc'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Supports PDF, JPG, PNG
                                </Typography>
                            </UploadPlaceholder>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 4, gap: 1 }}>
                    <Button onClick={() => setOpen(false)} sx={{ fontWeight: 700, borderRadius: '12px' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} sx={{ bgcolor: '#3DAC5A', borderRadius: '12px', fontWeight: 700, px: 4, '&:hover': { bgcolor: '#2d8a47' } }}>
                        Assign Asset
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!previewUrl} onClose={() => setPreviewUrl(null)} maxWidth="lg" PaperProps={{ sx: { borderRadius: '24px' } }}>
                <DialogTitle sx={{ m: 0, p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Document Preview</Typography>
                    <IconButton onClick={() => setPreviewUrl(null)} sx={{ bgcolor: alpha('#000', 0.05) }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, bgcolor: alpha('#000', 0.02) }}>
                    {previewUrl && (previewUrl.toLowerCase().endsWith('.pdf')) ? (
                        <iframe src={previewUrl} style={{ width: '100%', height: '80vh', border: 'none' }} title="Doc Preview" />
                    ) : (
                        <img src={previewUrl} alt="Preview" style={{ width: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block', margin: 'auto' }} />
                    )}
                </DialogContent>
            </Dialog>
        </GlassCard>
    );
};

export default AssetTab;
